import React, { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../../components/Layout';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Request failed' }));
    const e = new Error((Array.isArray(err.message) ? err.message.join('; ') : err.message) || 'Request failed') as Error & { status?: number };
    e.status = res.status; throw e;
  }
  if (res.status === 204) return null;
  return res.json();
}

const CP_LABEL: Record<string, string> = { '30_day': '30-day', '60_day': '60-day', '90_day': '90-day', '180_day': '180-day' };
const cpLabel = (k: string) => CP_LABEL[k] || (k?.startsWith('annual_') ? `Annual (${k.slice(7)})` : k);
const RATING = [{ v: 'exceeding', l: 'Exceeding expectations' }, { v: 'meeting', l: 'Meeting expectations' }, { v: 'not_yet', l: 'Not yet meeting expectations' }];
const CONSID = [{ v: '', l: '—' }, { v: 'yes', l: 'Yes' }, { v: 'not_now', l: 'Not now' }, { v: 'n_a', l: 'N/A' }];
const STATE_BADGE: Record<string, string> = {
  overdue: 'bg-red-100 text-red-800 border-red-300', due: 'bg-amber-100 text-amber-800 border-amber-300',
  upcoming: 'bg-blue-100 text-blue-800 border-blue-300',
};
const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700', waiting_for_employee: 'bg-indigo-100 text-indigo-800',
  ready_to_meet: 'bg-amber-100 text-amber-800', completed: 'bg-green-100 text-green-800', skipped: 'bg-gray-200 text-gray-500',
};
const STATUS_LABEL: Record<string, string> = {
  not_started: 'Not started', draft: 'Draft', waiting_for_employee: 'Waiting for employee',
  ready_to_meet: 'Ready to meet', completed: 'Completed', skipped: 'Skipped',
};
const fmt = (d: any) => (d ? new Date(String(d).length <= 10 ? `${String(d).slice(0, 10)}T00:00:00` : d).toLocaleDateString() : '—');

export function ReviewsPage() {
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState('');

  const load = async () => {
    setLoading(true); setError(''); setForbidden(false);
    try { setRows(await apiRequest('/reviews') || []); }
    catch (e: any) { if (e.status === 403) setForbidden(true); else setError(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const start = async (r: any) => {
    setBusy(r.employeeId + r.checkpointKey); setError('');
    try { const rev = await apiRequest('/reviews/start', { method: 'POST', body: JSON.stringify({ employeeId: r.employeeId, checkpointKey: r.checkpointKey }) }); await load(); setOpenId(rev.id); }
    catch (e: any) { setError(e.message); } finally { setBusy(''); }
  };
  const skip = async (r: any) => {
    const reason = window.prompt('Reason for skipping this review (required):');
    if (reason === null) return;
    if (!reason.trim()) { setError('A skip needs a written reason.'); return; }
    setBusy(r.employeeId + r.checkpointKey); setError('');
    try { await apiRequest('/reviews/skip', { method: 'POST', body: JSON.stringify({ employeeId: r.employeeId, checkpointKey: r.checkpointKey, reason: reason.trim() }) }); await load(); }
    catch (e: any) { setError(e.message); } finally { setBusy(''); }
  };

  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>;
  if (forbidden) return (
    <>
      <PageHeader title="Reviews" subtitle="Development reviews" />
      <Card><div className="text-center py-10 text-gray-600">Reviews are limited to employees granted Reviews access in User Management.</div></Card>
    </>
  );

  return (
    <>
      <PageHeader title="Reviews" subtitle="Development reviews — a two-way conversation, on a schedule. Overdue first. Not discipline." />
      {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}
      <Card>
        {rows.length === 0 ? <p className="text-gray-500 text-center py-10">No reviews due.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b"><tr className="text-left text-sm text-gray-600">
                <th className="px-4 py-3 font-medium">Employee</th>
                <th className="px-4 py-3 font-medium">Checkpoint</th>
                <th className="px-4 py-3 font-medium">Due</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y">
                {rows.map((r, i) => (
                  <tr key={r.id || `${r.employeeId}:${r.checkpointKey}:${i}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{r.employeeName}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{cpLabel(r.checkpointKey)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{fmt(r.dueDate)}</td>
                    <td className="px-4 py-3">
                      {r.persisted
                        ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[r.status] || ''}`}>{STATUS_LABEL[r.status] || r.status}</span>
                        : <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${STATE_BADGE[r.scheduleState] || ''}`}>{r.scheduleState}</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {r.persisted
                        ? <button onClick={() => setOpenId(r.id)} className="text-blue-600 hover:text-blue-700 font-medium text-sm">Open</button>
                        : <>
                            <button disabled={!!busy} onClick={() => start(r)} className="text-blue-600 hover:text-blue-700 font-medium text-sm mr-3">Start review</button>
                            <button disabled={!!busy} onClick={() => skip(r)} className="text-gray-500 hover:text-gray-700 text-sm">Skip</button>
                          </>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {openId && <ReviewModal id={openId} onClose={() => { setOpenId(null); load(); }} />}
    </>
  );
}

function Ta({ label, value, onChange, rows = 2, hint }: any) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}{hint && <span className="text-gray-400 font-normal"> — {hint}</span>}</label>
      <textarea rows={rows} value={value || ''} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><div className="text-xs uppercase tracking-wide text-gray-400 mb-0.5">{label}</div><div className="text-sm text-gray-900 whitespace-pre-wrap">{children}</div></div>;
}

function ReviewModal({ id, onClose }: { id: string; onClose: () => void }) {
  const [r, setR] = useState<any>(null);
  const [form, setForm] = useState<any>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [employees, setEmployees] = useState<any[]>([]);
  const [showFu, setShowFu] = useState(false);
  const [fu, setFu] = useState({ title: '', ownerId: '', dueDate: '' });
  const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm';

  const reload = async () => {
    const data = await apiRequest(`/reviews/${id}`); setR(data);
    setForm({
      overallRating: data.overallRating || '', overallExplanation: data.overallExplanation || '',
      strengths: data.strengths || '', recognition: data.recognition || '', areasToImprove: data.areasToImprove || '',
      trainingNeeds: data.trainingNeeds || '', supportProvided: data.supportProvided || '', goals: data.goals || '',
      stationsNow: data.stationsNow || '', nextStation: data.nextStation || '', crossTraining: data.crossTraining || '',
      raiseConsideration: data.management?.raiseConsideration || '', raiseNote: data.management?.raiseNote || '',
      promotionConsideration: data.management?.promotionConsideration || '', promotionNote: data.management?.promotionNote || '',
      leadershipConsideration: data.management?.leadershipConsideration || '', leadershipNote: data.management?.leadershipNote || '',
      correctiveActionWarranted: !!data.management?.correctiveActionWarranted,
    });
  };
  useEffect(() => { reload(); apiRequest('/employees?status=active').then((e) => setEmployees((e || []).map((x: any) => ({ id: x.id, name: x.name || `${x.first_name || ''} ${x.last_name || ''}`.trim() })))).catch(() => {}); }, [id]);
  if (!r) return <Modal title="Review" onClose={onClose}><div className="py-10 flex justify-center"><LoadingSpinner /></div></Modal>;

  const completed = r.status === 'completed';
  const skipped = r.status === 'skipped';
  const editable = !completed && !skipped;
  const act = async (key: string, fn: () => Promise<any>) => { setBusy(key); setError(''); try { await fn(); await reload(); } catch (e: any) { setError(e.message); } finally { setBusy(''); } };
  const saveDraft = () => act('save', () => apiRequest(`/reviews/${id}`, { method: 'PATCH', body: JSON.stringify(form) }));
  const setStatus = (status: string) => act('status', async () => { await apiRequest(`/reviews/${id}`, { method: 'PATCH', body: JSON.stringify(form) }); await apiRequest(`/reviews/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }); });
  const complete = () => act('complete', async () => { await apiRequest(`/reviews/${id}`, { method: 'PATCH', body: JSON.stringify(form) }); await apiRequest(`/reviews/${id}/complete`, { method: 'POST' }); });
  const openPdf = () => {
    const token = localStorage.getItem('admin_auth_token');
    fetch(`${API_BASE}/reviews/${id}/employee-copy.pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => res.blob()).then(b => { const u = URL.createObjectURL(b); window.open(u, '_blank'); setTimeout(() => URL.revokeObjectURL(u), 60000); });
  };
  const ev = r.evidence || {};

  return (
    <Modal title={`${cpLabel(r.checkpointKey)} review — ${r.employeeName}`} onClose={onClose}>
      <div className="space-y-5">
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[r.status] || ''}`}>{STATUS_LABEL[r.status] || r.status}</span>
          <span className="text-sm text-gray-500">Due {fmt(r.dueDate)} · period {fmt(r.reviewPeriodStart)}–{fmt(r.reviewPeriodEnd)}</span>
          <div className="ml-auto flex gap-2">
            <button onClick={openPdf} className="px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Print employee copy</button>
          </div>
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">{error}</div>}
        {skipped && <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 text-sm text-gray-700">Skipped — {r.skipReason}</div>}

        {/* Last time */}
        {r.lastTime && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
            <div className="font-semibold text-blue-900 mb-1">Last time ({cpLabel(r.lastTime.checkpointKey)})</div>
            {r.lastTime.goals && <div><span className="text-gray-500">Goals:</span> {r.lastTime.goals}</div>}
            {r.lastTime.trainingNeeds && <div><span className="text-gray-500">Training:</span> {r.lastTime.trainingNeeds}</div>}
            {(r.lastTime.followUps || []).length > 0 && (
              <ul className="mt-1">{r.lastTime.followUps.map((f: any, i: number) => <li key={i}>• {f.title} — <strong>{f.status}</strong>{f.ownerName ? ` (owner ${f.ownerName})` : ''}</li>)}</ul>
            )}
          </div>
        )}

        {/* Shared fields */}
        {editable ? (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Overall</label>
              <select value={form.overallRating} onChange={e => setForm({ ...form, overallRating: e.target.value })} className={input}>
                <option value="">Select…</option>{RATING.map(x => <option key={x.v} value={x.v}>{x.l}</option>)}
              </select>
            </div>
            <Ta label="Overall explanation" value={form.overallExplanation} onChange={(v: string) => setForm({ ...form, overallExplanation: v })} />
            <Ta label="Strengths" value={form.strengths} onChange={(v: string) => setForm({ ...form, strengths: v })} />
            <Ta label="Recognition" value={form.recognition} onChange={(v: string) => setForm({ ...form, recognition: v })} />
            <Ta label="Areas to improve" hint="specific and measurable" value={form.areasToImprove} onChange={(v: string) => setForm({ ...form, areasToImprove: v })} />
            <Ta label="Training needs" value={form.trainingNeeds} onChange={(v: string) => setForm({ ...form, trainingNeeds: v })} />
            <Ta label="Support the company will provide" value={form.supportProvided} onChange={(v: string) => setForm({ ...form, supportProvided: v })} />
            <Ta label="Goals for next period" hint="specific enough two people can't disagree whether it was met" value={form.goals} onChange={(v: string) => setForm({ ...form, goals: v })} />
            <div className="grid grid-cols-2 gap-3">
              <Ta label="Stations they can run now" value={form.stationsNow} onChange={(v: string) => setForm({ ...form, stationsNow: v })} />
              <Ta label="Next station to learn" value={form.nextStation} onChange={(v: string) => setForm({ ...form, nextStation: v })} />
            </div>
            <Ta label="Cross-training opportunities" value={form.crossTraining} onChange={(v: string) => setForm({ ...form, crossTraining: v })} />
          </div>
        ) : (
          <div className="space-y-3">
            <Field label="Overall">{RATING.find(x => x.v === r.overallRating)?.l || '—'}{r.overallExplanation ? ` — ${r.overallExplanation}` : ''}</Field>
            {r.strengths && <Field label="Strengths">{r.strengths}</Field>}
            {r.areasToImprove && <Field label="Areas to improve">{r.areasToImprove}</Field>}
            {r.goals && <Field label="Goals">{r.goals}</Field>}
            {r.trainingNeeds && <Field label="Training needs">{r.trainingNeeds}</Field>}
            {r.supportProvided && <Field label="Support provided">{r.supportProvided}</Field>}
          </div>
        )}

        {/* Employee's own answers */}
        <div className="border-t pt-4">
          <div className="text-sm font-semibold text-gray-700 mb-2">Employee's own feedback {r.employeeAnswers?.employeeInputAt ? '' : <span className="text-gray-400 font-normal">— not submitted yet</span>}</div>
          {r.employeeAnswers?.employeeInputAt ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {r.employeeAnswers.goingWell && <Field label="Going well">{r.employeeAnswers.goingWell}</Field>}
              {r.employeeAnswers.struggling && <Field label="Struggling with">{r.employeeAnswers.struggling}</Field>}
              {r.employeeAnswers.trainingHelp && <Field label="Training/help">{r.employeeAnswers.trainingHelp}</Field>}
              {r.employeeAnswers.otherRole && <Field label="Other role to learn">{r.employeeAnswers.otherRole}</Field>}
              {r.employeeAnswers.moreResponsibility && <Field label="More responsibility?">{r.employeeAnswers.moreResponsibility}</Field>}
              {r.employeeAnswers.inTheWay && <Field label="In the way">{r.employeeAnswers.inTheWay}</Field>}
              {r.employeeAnswers.anythingElse && <Field label="Anything else">{r.employeeAnswers.anythingElse}</Field>}
            </div>
          ) : <p className="text-sm text-gray-400">The employee hasn't given input. You can capture it in the meeting instead.</p>}
        </div>

        {/* Evidence (manager only, read-only) */}
        <div className="border-t pt-4">
          <div className="text-sm font-semibold text-gray-700 mb-2">Evidence for the period {completed ? '(frozen)' : '(live preview)'}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Field label="Attendance">{ev.attendance?.statement || `${ev.attendance?.daysPresent}/${ev.attendance?.availableWorkingDays} days`}</Field>
            <Field label="Work cards">{ev.production?.statement || `${ev.production?.workCards} (${ev.production?.workCardsPerDay ?? '—'}/day)`}</Field>
            <Field label="Discipline">{(ev.discipline?.warnings || []).length ? `${ev.discipline.warnings.length} on file` : 'None in period'}</Field>
            <Field label="Points">{ev.points?.totalPoints ?? 0} pts</Field>
          </div>
          <p className="text-xs text-gray-400 mt-1">{ev.points?.caveat}</p>
          <div className="mt-2 text-sm">
            <div className="text-xs uppercase tracking-wide text-gray-400 mb-0.5">Support requests they submitted (and what each turned out to be)</div>
            {(ev.supportRequests || []).length === 0 ? <div className="text-gray-400">None in period.</div> : (
              <ul className="space-y-1">{ev.supportRequests.map((s: any, i: number) => <li key={i}>{s.requestNumber}: {s.description} <span className="text-gray-500">→ {s.turnedOutToBe || 'open'}</span></li>)}</ul>
            )}
          </div>
        </div>

        {/* Management only */}
        <div className="border-t pt-4">
          <div className="text-sm font-semibold text-gray-700 mb-1">Management only <span className="text-gray-400 font-normal">— never shown to the employee</span></div>
          {editable ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><label className="block text-xs text-gray-500 mb-1">Raise consideration</label><select className={input} value={form.raiseConsideration} onChange={e => setForm({ ...form, raiseConsideration: e.target.value })}>{CONSID.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}</select></div>
              <div><label className="block text-xs text-gray-500 mb-1">Promotion consideration</label><select className={input} value={form.promotionConsideration} onChange={e => setForm({ ...form, promotionConsideration: e.target.value })}>{CONSID.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}</select></div>
              <div><label className="block text-xs text-gray-500 mb-1">Leadership potential</label><select className={input} value={form.leadershipConsideration} onChange={e => setForm({ ...form, leadershipConsideration: e.target.value })}>{CONSID.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}</select></div>
              <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-3"><input type="checkbox" checked={form.correctiveActionWarranted} onChange={e => setForm({ ...form, correctiveActionWarranted: e.target.checked })} /> Corrective action warranted</label>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <Field label="Raise">{r.management?.raiseConsideration || '—'}</Field>
              <Field label="Promotion">{r.management?.promotionConsideration || '—'}</Field>
              <Field label="Leadership">{r.management?.leadershipConsideration || '—'}</Field>
              <Field label="Corrective action">{r.management?.correctiveActionWarranted ? 'Yes' : 'No'}</Field>
            </div>
          )}
          {(form.correctiveActionWarranted || r.management?.correctiveActionWarranted) && (
            <a href={`/hr/discipline?issueWarning=${r.employeeId}`} className="inline-block mt-2 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">Open Issue Warning in Discipline</a>
          )}
          <p className="text-xs text-gray-400 mt-1">Considerations, not decisions. The review never creates anything in discipline.</p>
        </div>

        {/* Actions */}
        {editable && (
          <div className="border-t pt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-2">
              <button disabled={!!busy} onClick={saveDraft} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm">{busy === 'save' ? 'Saving…' : 'Save draft'}</button>
              {r.status === 'draft' && <button disabled={!!busy} onClick={() => setStatus('waiting_for_employee')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm">Ask employee for input</button>}
              {r.status !== 'ready_to_meet' && <button disabled={!!busy} onClick={() => setStatus('ready_to_meet')} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm">Ready to meet</button>}
            </div>
            <button disabled={!!busy} onClick={complete} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 text-sm">{busy === 'complete' ? 'Completing…' : 'Complete review'}</button>
          </div>
        )}

        {/* Completed: addenda + acknowledgment */}
        {completed && (
          <div className="border-t pt-4">
            <div className="text-sm font-semibold text-gray-700 mb-1">Acknowledgment</div>
            <div className="text-sm text-gray-600 mb-3">{r.acknowledgedAt ? `Acknowledged ${fmt(r.acknowledgedAt)}${r.employeeComment ? ` — "${r.employeeComment}"` : ''}` : 'Not yet acknowledged by the employee.'}</div>
            <div className="text-sm font-semibold text-gray-700 mb-1">Addenda <span className="text-gray-400 font-normal">— corrections to a completed review</span></div>
            {(r.addenda || []).length > 0 && <ul className="text-sm text-gray-600 mb-2 space-y-1">{r.addenda.map((a: any, i: number) => <li key={i}><strong>{a.fieldLabel}:</strong> {a.newValue} — {a.note} <span className="text-xs text-gray-400">({a.authorName}, {fmt(a.at)})</span></li>)}</ul>}
            <AddendumForm onAdd={(d) => act('add', () => apiRequest(`/reviews/${id}/addendum`, { method: 'POST', body: JSON.stringify(d) }))} busy={busy === 'add'} />
          </div>
        )}

        {/* Follow-ups from the review */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-gray-700">Create a follow-up from this review</div>
            {!showFu && <button onClick={() => setShowFu(true)} className="text-sm text-blue-600 hover:text-blue-700 font-medium">+ Create follow-up</button>}
          </div>
          {showFu && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
              <input className={input} placeholder="What needs doing (e.g. book saw-setup refresher)" value={fu.title} onChange={e => setFu({ ...fu, title: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <select className={input} value={fu.ownerId} onChange={e => setFu({ ...fu, ownerId: e.target.value })}><option value="">Owner…</option>{employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}</select>
                <input type="date" className={input} value={fu.dueDate} onChange={e => setFu({ ...fu, dueDate: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => { setShowFu(false); setFu({ title: '', ownerId: '', dueDate: '' }); }} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                <button disabled={!fu.title.trim() || !!busy} onClick={() => act('fu', async () => { await apiRequest('/follow-ups', { method: 'POST', body: JSON.stringify({ title: fu.title.trim(), ownerId: fu.ownerId || undefined, dueDate: fu.dueDate || r.dueDate, sourceType: 'review', sourceId: id, subjectEmployeeId: r.employeeId }) }); setShowFu(false); setFu({ title: '', ownerId: '', dueDate: '' }); })} className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded-lg disabled:opacity-50">Create follow-up</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function AddendumForm({ onAdd, busy }: { onAdd: (d: any) => void; busy: boolean }) {
  const [f, setF] = useState({ fieldLabel: '', newValue: '', note: '' });
  const input = 'px-3 py-2 border border-gray-300 rounded-lg text-sm';
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <input className={input} placeholder="Field" value={f.fieldLabel} onChange={e => setF({ ...f, fieldLabel: e.target.value })} />
      <input className={input} placeholder="New value" value={f.newValue} onChange={e => setF({ ...f, newValue: e.target.value })} />
      <div className="flex gap-2"><input className={`${input} flex-1`} placeholder="Reason" value={f.note} onChange={e => setF({ ...f, note: e.target.value })} />
        <button disabled={!f.fieldLabel.trim() || !f.newValue.trim() || !f.note.trim() || busy} onClick={() => { onAdd(f); setF({ fieldLabel: '', newValue: '', note: '' }); }} className="px-3 py-2 bg-gray-800 text-white rounded-lg text-sm disabled:opacity-50">Add</button></div>
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-start justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full my-8">
          <div className="border-b px-6 py-4 flex items-center justify-between sticky top-0 bg-white rounded-t-xl z-10">
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
          </div>
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default ReviewsPage;
